import { useEventListener } from '@fullstory/react-hooks';
import React, {
  HTMLAttributes,
  MutableRefObject,
  ReactElement,
  RefCallback,
  useCallback,
  useRef,
} from 'react';

type MouseEvents = 'click' | 'mousedown' | 'mouseup';
type TouchEvents = 'touchstart' | 'touchend';
type Events = MouseEvent | TouchEvent;
interface ClickAwayListenerProps extends HTMLAttributes<HTMLElement> {
  onClickAway: (event: Events) => void;
  /**
   * Optionally pass an anchor element to prevent clicks on the anchor from
   * triggering the `onClickAway` callback.
   */
  anchor?: Element | null;
  mouseEvent?: MouseEvents;
  touchEvent?: TouchEvents;
  children: ReactElement<any>;
}

type BubbledEvent = {
  event: Events;
  type: string;
};

export const ClickAwayListener = React.forwardRef<HTMLElement, ClickAwayListenerProps>(
  (
    { children, onClickAway, anchor, mouseEvent = 'mousedown', touchEvent = 'touchstart' },
    forwardedRef,
  ) => {
    const node: MutableRefObject<HTMLElement | null> = useRef(null);
    const bubbled: MutableRefObject<boolean> = useRef(false);

    const handleBubbledEvents = ({ event, type }: BubbledEvent): void => {
      bubbled.current = true;
      const handler = children?.props[type];
      if (handler) {
        handler(event);
      }
    };

    const handler = useCallback(
      (event: Events): void => {
        // if click bubbled up from within the ClickAwayListener, the node contains the
        // event target, or the click is in the anchor el, don't fire click away event
        if (
          bubbled.current ||
          (node.current && node.current.contains(event.target as Node)) ||
          (anchor && anchor.contains(event.target as Node))
        ) {
          bubbled.current = false;
          return;
        }

        onClickAway(event);
      },
      [anchor, onClickAway],
    );

    // register global event listeners
    useEventListener({
      name: mouseEvent,
      handler,
    });
    useEventListener({
      name: touchEvent,
      handler,
    });

    return React.Children.only(
      React.cloneElement(children as ReactElement<any>, {
        ref: (childRef: HTMLElement) => {
          node.current = childRef;

          const { ref } = children as typeof children & {
            ref: RefCallback<HTMLElement> | MutableRefObject<HTMLElement>;
          };

          [ref, forwardedRef].forEach(_ref => {
            if (typeof _ref === 'function') {
              _ref(childRef);
            } else if (_ref) {
              _ref.current = childRef;
            }
          });
        },
        onMouseDown: (event: Events) => handleBubbledEvents({ type: 'onMouseDown', event }),
        onTouchStart: (event: Events) => handleBubbledEvents({ type: 'onTouchStart', event }),
      }),
    );
  },
);

ClickAwayListener.displayName = 'ClickAwayListener';
